import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Luggage, FileCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface RelatedDestinationsProps {
  category?: string;
  tags?: string[];
}

const allDestinations = [
  { name: "Japan", slug: "japan", region: "Asia", type: ["culture", "adventure", "solo"] },
  { name: "Thailand", slug: "thailand", region: "Asia", type: ["beach", "budget", "backpacking"] },
  { name: "Italy", slug: "italy", region: "Europe", type: ["culture", "history", "food", "romantic"] },
  { name: "Greece", slug: "greece", region: "Europe", type: ["beach", "history", "islands"] },
  { name: "Morocco", slug: "morocco", region: "Africa", type: ["adventure", "culture", "desert"] },
  { name: "Bali", slug: "bali", region: "Asia", type: ["beach", "wellness", "digital nomad"] },
  { name: "Portugal", slug: "portugal", region: "Europe", type: ["beach", "history", "digital nomad"] },
  { name: "Vietnam", slug: "vietnam", region: "Asia", type: ["budget", "backpacking", "food"] },
  { name: "Spain", slug: "spain", region: "Europe", type: ["beach", "culture", "food"] },
  { name: "Peru", slug: "peru", region: "South America", type: ["adventure", "hiking", "culture"] },
  { name: "New Zealand", slug: "new-zealand", region: "Oceania", type: ["adventure", "hiking", "nature"] },
  { name: "Iceland", slug: "iceland", region: "Europe", type: ["adventure", "nature", "northern lights"] },
];

// Map blog categories/tags to destination types
const categoryMapping: Record<string, string[]> = {
  "Travel Tips": ["budget", "backpacking"],
  "Adventure": ["adventure", "hiking", "nature"],
  "Budget Travel": ["budget", "backpacking"],
  "Solo Travel": ["solo", "adventure"],
  "Digital Nomad": ["digital nomad"],
  "Health & Wellness": ["wellness", "beach"],
  "Sustainable Travel": ["nature", "adventure"],
  "Packing": ["culture", "adventure"],
};

export const RelatedDestinations = ({ category, tags = [] }: RelatedDestinationsProps) => {
  const navigate = useNavigate();

  // Get relevant destinations based on category and tags
  const getRelevantDestinations = () => {
    const relevantTypes = new Set<string>();
    
    // Add types based on category
    if (category && categoryMapping[category]) {
      categoryMapping[category].forEach(type => relevantTypes.add(type));
    }
    
    // Add types based on tags
    tags.forEach(tag => {
      const lowerTag = tag.toLowerCase();
      allDestinations.forEach(dest => {
        dest.type.forEach(type => {
          if (type.includes(lowerTag) || lowerTag.includes(type)) {
            relevantTypes.add(type);
          }
        });
      });
    });

    // Score and sort destinations
    const scored = allDestinations.map(dest => {
      let score = 0;
      dest.type.forEach(type => {
        if (relevantTypes.has(type)) score += 2;
      });
      return { ...dest, score };
    });

    // Return top 4 destinations, with some randomization for variety
    return scored
      .filter(d => d.score > 0)
      .sort((a, b) => b.score - a.score || Math.random() - 0.5)
      .slice(0, 4);
  };

  const destinations = getRelevantDestinations();

  // If no relevant destinations found, show random popular ones
  const displayDestinations = destinations.length > 0 
    ? destinations 
    : allDestinations.slice(0, 4);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <MapPin className="h-5 w-5 text-primary" />
          Related Destinations
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {displayDestinations.map((dest) => (
          <div 
            key={dest.slug}
            className="flex items-center justify-between p-2 rounded-lg hover:bg-secondary/50 transition-colors"
          >
            <div>
              <p className="font-medium text-sm">{dest.name}</p>
              <Badge variant="outline" className="text-xs mt-1">
                {dest.region}
              </Badge>
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => navigate(`/packing-list/${dest.slug}`)}
                title={`${dest.name} Packing List`}
              >
                <Luggage className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => navigate(`/visa/${dest.slug}`)}
                title={`${dest.name} Visa Guide`}
              >
                <FileCheck className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
        
        <Button 
          variant="link" 
          className="w-full text-primary text-sm"
          onClick={() => navigate('/')}
        >
          Explore all destinations →
        </Button>
      </CardContent>
    </Card>
  );
};
